import type { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

export type FindingBriefSectionVariant = 'summary' | 'measures' | 'followup';

/** Canonical names for the three parts of every verification brief. */
export const FINDING_BRIEF_SECTION_LABEL: Record<FindingBriefSectionVariant, string> = {
  summary: 'Case summary',
  measures: 'Review measures',
  followup: 'Follow-up evidence',
};

export const VERIFICATION_BRIEF_WORKFLOW_SUBTITLE =
  'One issue at a time — each brief includes case summary, review measures, and follow-up evidence.';

export function verificationBriefIntro(issueCount: number): string {
  if (issueCount === 1) {
    return 'Open the issue below — case summary, review measures, and follow-up evidence.';
  }
  return `${issueCount} issues — open one at a time. Each brief includes case summary, review measures, and follow-up evidence.`;
}

const FOLLOW_UP_ACCENT_LIGHT = '#047857';
const FOLLOW_UP_ACCENT_DARK = '#34d399';

/** Opaque scrim panels for evidence/summary — no backdrop-filter on scroll content. */
export function findingBriefSectionSx(
  variant: FindingBriefSectionVariant,
  accent: string,
  isLight: boolean,
): SxProps<Theme> {
  const followAccent = isLight ? FOLLOW_UP_ACCENT_LIGHT : FOLLOW_UP_ACCENT_DARK;
  const sectionAccent = variant === 'followup' ? followAccent : accent;

  const bg =
    variant === 'summary'
      ? alpha(sectionAccent, isLight ? 0.06 : 0.14)
      : variant === 'measures'
        ? alpha(sectionAccent, isLight ? 0.09 : 0.18)
        : alpha(followAccent, isLight ? 0.07 : 0.14);

  const solidBg =
    variant === 'followup'
      ? isLight
        ? '#ecfdf5'
        : '#052e16'
      : isLight
        ? '#f8fafc'
        : '#111827';

  return (theme) => ({
    p: 1,
    borderRadius: 2,
    bgcolor: bg,
    border: '1px solid',
    borderColor: alpha(sectionAccent, isLight ? 0.22 : 0.32),
    borderLeft: '3px solid',
    borderLeftColor: sectionAccent,
    '@media (prefers-reduced-transparency: reduce)': {
      bgcolor: solidBg,
      borderColor: alpha(sectionAccent, isLight ? 0.38 : 0.48),
    },
    '@media (prefers-contrast: more)': {
      bgcolor: isLight ? theme.palette.background.paper : theme.palette.background.default,
      borderColor: sectionAccent,
      borderLeftWidth: 4,
    },
  });
}

export function findingBriefSectionLabelSx(
  variant: FindingBriefSectionVariant,
  accent: string,
  isLight: boolean,
): SxProps<Theme> {
  const followAccent = isLight ? FOLLOW_UP_ACCENT_LIGHT : FOLLOW_UP_ACCENT_DARK;
  const sectionAccent = variant === 'followup' ? followAccent : accent;
  return {
    fontWeight: 700,
    letterSpacing: '0.01em',
    display: 'block',
    mb: 0.4,
    color: sectionAccent,
    fontSize: '0.8125rem',
    lineHeight: 1.35,
  };
}
